import {
  EMAIL_PANEL_STATES, emailPanelControls, emailPanelNote,
  EMAIL_CHANGE_TITLE, EMAIL_CHANGE_LEDE, EMAIL_CHANGE_SAVE_LABEL, EMAIL_CONTACT_LABEL,
} from "../emailChange.js";
import { UNDELIVERABLE_PROMPT_TITLE, UNDELIVERABLE_PROMPT_BODY } from "../emailAddress.js";

// THE EMAIL-CHANGE DOOR'S FACE. **HOOKLESS, SO THE GATE CALLS IT** once per state and reads the
// tree back — the same split `SmsSettingsPanel` ships on, and the reason a control this house
// cannot render in a fixture ends up being checked by a regex over a twelve-thousand-line file.
//
// **THE UNDELIVERABLE PROMPT MOVED IN HERE, AND THAT IS A CONSOLIDATION RATHER THAN A RELOCATION.**
// It stood in `AccountMenuPanel` pointing at the CONTACT FORM, because there was no other door —
// and its own copy ends *"Check the spelling below and save it"*, which described a field that did
// not exist. The prompt and the field are one surface now, so the word *below* is literally true,
// and the panel that knows whether she CAN change it is the one that decides what the prompt
// offers: the contact button survives for exactly the two faces that cannot (R18).
//
// **IT KEEPS THE PROMPT'S POSITION, WHICH IS R87's OWN RULING** — above the subscription block,
// because a member reading her account menu is here to find out what is going on with her account,
// and *we cannot reach you* outranks *what tier you are on*.
export function EmailSettingsPanel({
  state,
  email = "",
  value = "",
  hint = "",
  suggestion = "",
  note = "",
  undeliverable = false,
  busy = false,
  onValue = () => {},
  onSave = () => {},
  onContact = () => {},
}: {
  state: string;
  email?: string;
  value?: string;
  hint?: string;
  suggestion?: string;
  note?: string;
  undeliverable?: boolean;
  busy?: boolean;
  onValue?: (next: string) => void;
  onSave?: () => void;
  onContact?: () => void;
}) {
  const controls = emailPanelControls(state);
  const standing = emailPanelNote(state);

  return (
    <div className="account-email" data-email-panel={state}>
      {/* Presence, never truthiness (R53/R136/R137): this flag rides a payload and
          `Boolean("false")` is `true` — a coerced read tells a member this house cannot reach her
          when it can, which is a frightening sentence to invent out of a stale field. */}
      {undeliverable === true ? (
        <div className="account-email__alarm" data-account-email="undeliverable" role="note">
          <strong>{UNDELIVERABLE_PROMPT_TITLE}</strong>
          <span>{UNDELIVERABLE_PROMPT_BODY}</span>
        </div>
      ) : null}

      <p className="account-email__title">{EMAIL_CHANGE_TITLE}</p>
      {/* THE ADDRESS SHE IS ON IS ALWAYS SHOWN, WHATEVER THE FACE. A member who cannot change it
          here still came to find out what it is. */}
      <p className="account-email__current">{email}</p>
      <p className="account-email__lede">{EMAIL_CHANGE_LEDE}</p>

      {standing ? <p className="account-email__standing">{standing}</p> : null}

      {controls.field ? (
        <>
          {/* PRE-FILLED WITH HER OWN ADDRESS, WHICH IS THE WHOLE POINT FOR THE MEMBERS THIS IS FOR:
              three of the fifteen are one character short of correct, and retyping an address from
              memory is how the wrong one got there. */}
          <input
            type="email"
            className="account-email__field"
            value={value}
            disabled={busy}
            autoComplete="email"
            aria-label={EMAIL_CHANGE_TITLE}
            onChange={(event) => onValue(event.target.value)}
          />
          {/* The live hint is `emailFieldState`'s, run by the shell — the SAME law the signup field
              runs (R28/R29), so this house cannot hold two opinions about what an address is. */}
          {hint ? <p className="account-email__hint">{hint}{suggestion ? ` (${suggestion})` : ""}</p> : null}
          <button type="button" className="account-email__save" disabled={busy} onClick={onSave}>
            {EMAIL_CHANGE_SAVE_LABEL}
          </button>
        </>
      ) : null}

      {/* The door that really moves a member who cannot change it herself. It is the button the
          undeliverable prompt carried before this panel existed, kept for exactly the faces that
          still need it rather than dropped. */}
      {controls.contact ? (
        <button type="button" className="account-email__contact" onClick={onContact}>
          {/* M2 — her wording, and a DECLARATION rather than a literal: a label typed into markup is
              one no gate can read and no catalog can translate. The door is unchanged. */}
          {EMAIL_CONTACT_LABEL}
        </button>
      ) : null}

      {/* The door's own answer, saved or refused. It is `aria-live` because she pressed a button and
          the result of that press is the whole reason she is on this panel. */}
      {note ? <p className="account-email__note" role="status" aria-live="polite">{note}</p> : null}
    </div>
  );
}

export { EMAIL_PANEL_STATES };
